import type { Component } from 'vue'
import type { CanvasNodeKind, CanvasNodeContext } from './salesPlayCanvasContext'
import { apiFetch } from '../../services/api'

// ── Public types ────────────────────────────────────────────────

export interface CanvasDrawerCardDefinition {
  id: string
  component: Component
  resolveProps: (context: CanvasNodeContext) => Record<string, any>
}

export interface CanvasNodeDefinition {
  type: string
  kind: CanvasNodeKind
  actionType?: 'sequence' | 'dialer'
  operatesOn?: 'company' | 'person'

  /** How this node appears in the (+) add menu. */
  addMenu: {
    category: string
    label: string
    description: string
    icon: string
  }

  /** How this node renders inside a SalesPlayCanvasCard. */
  canvasCard: {
    /** AppIcon name for the card icon. */
    icon: string
    /** Derive title + subtitle from the node's config. */
    summarize: (config: Record<string, any>) => { title: string; subtitle: string }
    /** Vue component rendered in the card's #details slot.
     *  Receives props: { config: Record<string, any> }.
     *  Optional — omit if the node has no detail section. */
    detailsComponent?: Component
  }

  /** Drawer cards shown when this node is selected.
   *  Self-contained — no reference to the shared drawer registry. */
  drawerCards: CanvasDrawerCardDefinition[]

  /** Returns the initial config blob when the user adds this node. */
  createDefaultConfig: () => Record<string, any>
}

/** Runtime state for a node instance on the canvas. */
export interface CanvasNode {
  id: string
  type: string
  kind: CanvasNodeKind
  actionType?: 'sequence' | 'dialer'
  config: Record<string, any>
}

// ── Vue-specific overrides (components, summarizers, default configs) ──

import SfdcReportCanvasCard from '../sales-play/canvas/SfdcReportCanvasCard.vue'
import SfdcReportDrawerCard from '../sales-play/drawer/SfdcReportDrawerCard.vue'
import SfdcChampionsCanvasCard from '../sales-play/canvas/SfdcChampionsCanvasCard.vue'
import SfdcChampionsDrawerCard from '../sales-play/drawer/SfdcChampionsDrawerCard.vue'
import HubspotChampionsCanvasCard from '../sales-play/canvas/HubspotChampionsCanvasCard.vue'
import HubspotChampionsDrawerCard from '../sales-play/drawer/HubspotChampionsDrawerCard.vue'
import ProspectCanvasCard from '../sales-play/canvas/ProspectCanvasCard.vue'
import ProspectDrawerCard from '../sales-play/drawer/ProspectDrawerCard.vue'
import SequenceCanvasCard from '../sales-play/canvas/SequenceCanvasCard.vue'
import SequenceDrawerCard from '../sales-play/drawer/SequenceDrawerCard.vue'
import DialerCanvasCard from '../sales-play/canvas/DialerCanvasCard.vue'
import DialerDrawerCard from '../sales-play/drawer/DialerDrawerCard.vue'
import IcpCanvasCard from '../sales-play/canvas/IcpCanvasCard.vue'
import IcpDrawerCard from '../sales-play/drawer/IcpDrawerCard.vue'
import SfdcSearchCanvasCard from '../sales-play/canvas/SfdcSearchCanvasCard.vue'
import SfdcSearchDrawerCard from '../sales-play/drawer/SfdcSearchDrawerCard.vue'
import FilterModeDrawerCard from '../sales-play/drawer/FilterModeDrawerCard.vue'
import WarmlyEventCanvasCard from '../sales-play/canvas/WarmlyEventCanvasCard.vue'
import WarmlyEventDrawerCard from '../sales-play/drawer/WarmlyEventDrawerCard.vue'
import LeadiqAgentCanvasCard from '../sales-play/canvas/LeadiqAgentCanvasCard.vue'
import LeadiqAgentDrawerCard from '../sales-play/drawer/LeadiqAgentDrawerCard.vue'

/**
 * Vue-specific behavior for block types. These override the generic
 * defaults with block-specific summarizers, detail components, drawer
 * cards, and default configs.
 *
 * Metadata (icon, label, description, category) comes from the backend
 * via GET /block-metadata — do NOT set those here.
 */
interface CanvasNodeOverride {
  canvasCard?: {
    summarize?: (config: Record<string, any>) => { title: string; subtitle: string }
    detailsComponent?: Component
  }
  drawerCards?: CanvasDrawerCardDefinition[]
  createDefaultConfig?: () => Record<string, any>
}

const _drawerPropsFromCtx = (ctx: CanvasNodeContext) => ({
  playId: ctx.playId,
  pipelineId: ctx.pipelineId,
  nodeIndex: ctx.nodeIndex,
})

const canvasNodeOverrides: Record<string, CanvasNodeOverride> = {
  sfdc_report: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || config.report_name || 'Salesforce Report',
        subtitle: 'Salesforce Report',
      }),
      detailsComponent: SfdcReportCanvasCard,
    },
    drawerCards: [
      { id: 'sfdc_report_config', component: SfdcReportDrawerCard, resolveProps: _drawerPropsFromCtx },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_report', name: '', report_id: '', report_name: '' }),
  },

  sfdc_account_search: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Salesforce Accounts',
        subtitle: 'Salesforce \u00B7 Account',
      }),
      detailsComponent: SfdcSearchCanvasCard,
    },
    drawerCards: [
      {
        id: 'sfdc_account_search_config',
        component: SfdcSearchDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
          placeholder: 'SELECT Id, Name, Industry FROM Account WHERE ...',
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_account_search', name: '', soql: '' }),
  },

  sfdc_contact_search: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Salesforce Contacts',
        subtitle: 'Salesforce \u00B7 Contact',
      }),
      detailsComponent: SfdcSearchCanvasCard,
    },
    drawerCards: [
      {
        id: 'sfdc_contact_search_config',
        component: SfdcSearchDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
          placeholder: 'SELECT Id, Email, Name FROM Contact WHERE ...',
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_contact_search', name: '', soql: '' }),
  },

  sfdc_champions_search: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Salesforce Champions',
        subtitle: 'Salesforce \u00B7 Champion Tracking',
      }),
      detailsComponent: SfdcChampionsCanvasCard,
    },
    drawerCards: [
      {
        id: 'sfdc_champions_search_config',
        component: SfdcChampionsDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
          placeholder: 'SELECT Id, Email, Name FROM Contact WHERE ...',
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_champions_search', name: '', soql: '', report_id: '', report_name: '', mode: 'report' }),
  },


  sfdc_lead_search: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Salesforce Leads',
        subtitle: 'Salesforce \u00B7 Lead',
      }),
      detailsComponent: SfdcSearchCanvasCard,
    },
    drawerCards: [
      {
        id: 'sfdc_lead_search_config',
        component: SfdcSearchDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
          placeholder: 'SELECT Id, Email, Name FROM Lead WHERE ...',
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_lead_search', name: '', soql: '' }),
  },

  sfdc_account_filter: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Salesforce Accounts',
        subtitle: 'Salesforce \u00B7 Account',
      }),
      detailsComponent: SfdcSearchCanvasCard,
    },
    drawerCards: [
      { id: 'sfdc_account_filter_mode', component: FilterModeDrawerCard, resolveProps: _drawerPropsFromCtx },
      {
        id: 'sfdc_account_filter_config',
        component: SfdcSearchDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
          placeholder: 'SELECT Id, Name, Industry FROM Account WHERE ...',
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_account_filter', name: '', soql: '', mode: 'keep_matching' }),
  },

  sfdc_contact_filter: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Salesforce Contacts',
        subtitle: 'Salesforce \u00B7 Contact',
      }),
      detailsComponent: SfdcSearchCanvasCard,
    },
    drawerCards: [
      { id: 'sfdc_contact_filter_mode', component: FilterModeDrawerCard, resolveProps: _drawerPropsFromCtx },
      {
        id: 'sfdc_contact_filter_config',
        component: SfdcSearchDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
          placeholder: 'SELECT Id, Email, Name FROM Contact WHERE ...',
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_contact_filter', name: '', soql: '', mode: 'keep_matching' }),
  },

  sfdc_lead_filter: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Salesforce Leads',
        subtitle: 'Salesforce \u00B7 Lead',
      }),
      detailsComponent: SfdcSearchCanvasCard,
    },
    drawerCards: [
      { id: 'sfdc_lead_filter_mode', component: FilterModeDrawerCard, resolveProps: _drawerPropsFromCtx },
      {
        id: 'sfdc_lead_filter_config',
        component: SfdcSearchDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
          placeholder: 'SELECT Id, Email, Name FROM Lead WHERE ...',
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'sfdc_lead_filter', name: '', soql: '', mode: 'keep_matching' }),
  },

  hubspot_champions_search: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Hubspot Champions',
        subtitle: 'Hubspot \u00B7 Champion Tracking',
      }),
      detailsComponent: HubspotChampionsCanvasCard,
    },
    drawerCards: [
      {
        id: 'hubspot_champions_search_config',
        component: HubspotChampionsDrawerCard,
        resolveProps: (ctx) => ({
          ...(_drawerPropsFromCtx(ctx)),
        }),
      },
    ],
    createDefaultConfig: () => ({ type: 'hubspot_champions_search', name: '', segment_id: '', segment_name: '' }),
  },




  warmly_website_visit_contact: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Website Visit Contact',
        subtitle: 'Warmly \u00B7 Contact',
      }),
      detailsComponent: WarmlyEventCanvasCard,
    },
    drawerCards: [
      { id: 'warmly_website_visit_contact_config', component: WarmlyEventDrawerCard, resolveProps: (ctx) => ({ ..._drawerPropsFromCtx(ctx), signalTypes: ['warmly.website_visit_contact'] }) },
    ],
    createDefaultConfig: () => ({ type: 'warmly_website_visit_contact', name: '', window: { last: { days: 14 } } }),
  },

  warmly_website_visit_company: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Website Visit Company',
        subtitle: 'Warmly \u00B7 Company',
      }),
      detailsComponent: WarmlyEventCanvasCard,
    },
    drawerCards: [
      { id: 'warmly_website_visit_company_config', component: WarmlyEventDrawerCard, resolveProps: (ctx) => ({ ..._drawerPropsFromCtx(ctx), signalTypes: ['warmly.website_visit_company'] }) },
    ],
    createDefaultConfig: () => ({ type: 'warmly_website_visit_company', name: '', window: { last: { days: 14 } } }),
  },

  warmly_bombora_research_intent: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Bombora Research Intent',
        subtitle: 'Warmly \u00B7 Intent',
      }),
      detailsComponent: WarmlyEventCanvasCard,
    },
    drawerCards: [
      { id: 'warmly_bombora_research_intent_config', component: WarmlyEventDrawerCard, resolveProps: (ctx) => ({ ..._drawerPropsFromCtx(ctx), signalTypes: ['warmly.bombora_research_intent'] }) },
    ],
    createDefaultConfig: () => ({ type: 'warmly_bombora_research_intent', name: '', window: { last: { days: 14 } } }),
  },

  leadiq_agent_find_companies: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'LeadIQ Agent',
        subtitle: 'LeadIQ \u00B7 Find Companies',
      }),
      detailsComponent: LeadiqAgentCanvasCard,
    },
    drawerCards: [
      { id: 'leadiq_agent_find_companies_config', component: LeadiqAgentDrawerCard, resolveProps: _drawerPropsFromCtx },
    ],
    createDefaultConfig: () => ({ type: 'leadiq_agent_find_companies', name: '', prompt: '', num_results: 25 }),
  },


  leadiq_agent_qualify_companies: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'LeadIQ Agent',
        subtitle: 'LeadIQ \u00B7 Qualify Companies',
      }),
      detailsComponent: LeadiqAgentCanvasCard,
    },
    drawerCards: [
      { id: 'leadiq_agent_qualify_companies_mode', component: FilterModeDrawerCard, resolveProps: _drawerPropsFromCtx },
      { id: 'leadiq_agent_qualify_companies_config', component: LeadiqAgentDrawerCard, resolveProps: (ctx) => ({ ..._drawerPropsFromCtx(ctx), showNumResults: false }) },
    ],
    createDefaultConfig: () => ({ type: 'leadiq_agent_qualify_companies', name: '', prompt: '', mode: 'keep_matching' }),
  },

  leadiq_agent_qualify_people: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'LeadIQ Agent',
        subtitle: 'LeadIQ \u00B7 Qualify People',
      }),
      detailsComponent: LeadiqAgentCanvasCard,
    },
    drawerCards: [
      { id: 'leadiq_agent_qualify_people_mode', component: FilterModeDrawerCard, resolveProps: _drawerPropsFromCtx },
      { id: 'leadiq_agent_qualify_people_config', component: LeadiqAgentDrawerCard, resolveProps: (ctx) => ({ ..._drawerPropsFromCtx(ctx), showNumResults: false }) },
    ],
    createDefaultConfig: () => ({ type: 'leadiq_agent_qualify_people', name: '', prompt: '', mode: 'keep_matching' }),
  },

  icp: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Ideal Customer Profile',
        subtitle: 'ICP Filter',
      }),
      detailsComponent: IcpCanvasCard,
    },
    drawerCards: [
      { id: 'icp_mode', component: FilterModeDrawerCard, resolveProps: _drawerPropsFromCtx },
      { id: 'icp_config', component: IcpDrawerCard, resolveProps: _drawerPropsFromCtx },
    ],
    createDefaultConfig: () => ({ name: '', icp: { company_size: '50-2000 employees' }, mode: 'keep_matching' }),
  },

  // ── Non-block UI nodes (prospect, sequence) ──────────────────
  // These are NOT Python blocks — they're UI-only concepts.
  // Their full definition is hardcoded here, not fetched from API.

  prospect: {
    canvasCard: {
      summarize: () => ({
        title: 'Prospect',
        subtitle: 'Raven Agent',
      }),
      detailsComponent: ProspectCanvasCard,
    },
    drawerCards: [
      { id: 'prospect_config', component: ProspectDrawerCard, resolveProps: _drawerPropsFromCtx },
    ],
    createDefaultConfig: () => ({ approach: '', personas: [], active_days: 1, opportunistic_days: 7 }),
  },

  sequence: {
    canvasCard: {
      summarize: (config) => ({
        title: config.name || 'Sequence',
        subtitle: 'Sequence',
      }),
      detailsComponent: SequenceCanvasCard,
    },
    drawerCards: [
      { id: 'sequence_config', component: SequenceDrawerCard, resolveProps: _drawerPropsFromCtx },
    ],
    createDefaultConfig: () => ({
      name: '', sequence_id: '', sequence_description: '',
      // A brand-new sequence node starts with one email step rather than
      // an empty (invalid) sequence, so it's ready to run without an extra
      // required trip through the sequence editor first.
      steps: [
        { step_index: 0, step_type: 'email', brief: '', wait_days: null, wait_unit: null },
      ],
      sender_approach: '',
      scheduling_customized: false,
      scheduling_timezone_aware_sending: false,
      scheduling_default_us_timezone: '',
      scheduling_default_send_timezone: '',
    }),
  },

  dialer: {
    canvasCard: {
      summarize: () => ({
        title: 'Power Dialer',
        subtitle: 'Power Dialer',
      }),
      detailsComponent: DialerCanvasCard,
    },
    drawerCards: [
      { id: 'dialer_config', component: DialerDrawerCard, resolveProps: _drawerPropsFromCtx },
    ],
    /* No sales play level config yet for the dialer */
    createDefaultConfig: () => ({}),
  },
}

// ── Static definitions for non-block nodes ─────────────────────

const _staticNodes: Record<string, CanvasNodeDefinition> = {
  prospect: {
    type: 'prospect',
    kind: 'prospect',
    addMenu: { category: 'Prospect', label: 'Prospect', description: 'Configure the Raven agent to research candidates', icon: 'raven' },
    canvasCard: {
      icon: 'raven',
      summarize: canvasNodeOverrides.prospect.canvasCard!.summarize!,
      detailsComponent: ProspectCanvasCard,
    },
    drawerCards: canvasNodeOverrides.prospect.drawerCards!,
    createDefaultConfig: canvasNodeOverrides.prospect.createDefaultConfig!,
  },
  sequence: {
    type: 'sequence',
    kind: 'action',
    actionType: 'sequence',
    addMenu: { category: 'Action', label: 'Sequence', description: 'Enroll candidates into an email/LinkedIn/voicemail sequence', icon: 'sequence' },
    canvasCard: {
      icon: 'sequence',
      summarize: canvasNodeOverrides.sequence.canvasCard!.summarize!,
      detailsComponent: SequenceCanvasCard,
    },
    drawerCards: canvasNodeOverrides.sequence.drawerCards!,
    createDefaultConfig: canvasNodeOverrides.sequence.createDefaultConfig!,
  },
  dialer: {
    type: 'dialer',
    kind: 'action',
    actionType: 'dialer',
    addMenu: { category: 'Action', label: 'Power Dialer', description: 'Make candidates available in the power dialer queue', icon: 'phone_call' },
    canvasCard: {
      icon: 'phone_call',
      summarize: canvasNodeOverrides.dialer.canvasCard!.summarize!,
      detailsComponent: DialerCanvasCard,
    },
    drawerCards: canvasNodeOverrides.dialer.drawerCards!,
    createDefaultConfig: canvasNodeOverrides.dialer.createDefaultConfig!,
  },
}

// ── Runtime registry (populated by initBlockRegistry) ──────────

/** The merged registry: API metadata + Vue overrides + static nodes. */
let _registry: Record<string, CanvasNodeDefinition> = { ..._staticNodes }
let _initialized = false

/** Default summarizer for blocks that have no Vue override. */
function _defaultSummarize(label: string) {
  return (config: Record<string, any>) => ({
    title: config.name || label,
    subtitle: label,
  })
}

/** Map API slots + category to a CanvasNodeKind. */
function _slotToKind(slots: string[], category: string): CanvasNodeKind {
  if (slots.includes('produce')) return 'source'
  if (category === 'Action') return 'action'
  if (category === 'Prospect') return 'prospect'
  return 'filter'
}

/**
 * Fetch block metadata from the backend and merge with Vue overrides.
 * Call this once on app/page init before the canvas renders.
 */
export async function initBlockRegistry(): Promise<void> {
  if (_initialized) return
  try {
    const resp: any = await apiFetch('/block-metadata')
    const blocks: Record<string, any> = resp?.blocks || {}

    for (const [bt, meta] of Object.entries(blocks)) {
      // Skip blocks that are already static non-block nodes.
      if (bt in _staticNodes) continue
      // Skip unpublished blocks.
      if (!meta.published) continue

      const override = canvasNodeOverrides[bt]
      const kind = _slotToKind(meta.slots || [], meta.category || '')

      _registry[bt] = {
        type: bt,
        kind,
        operatesOn: kind === 'filter' ? (meta.operates_on || undefined) : undefined,
        addMenu: {
          category: meta.category || (kind === 'source' ? 'Find' : 'Qualify'),
          label: meta.label || bt,
          description: meta.description || '',
          icon: meta.icon || '',
        },
        canvasCard: {
          icon: meta.icon || '',
          summarize: override?.canvasCard?.summarize || _defaultSummarize(meta.label || bt),
          detailsComponent: override?.canvasCard?.detailsComponent,
        },
        drawerCards: override?.drawerCards || [],
        createDefaultConfig: override?.createDefaultConfig || (() => ({})),
      }
    }
    _initialized = true
  } catch (e) {
    // On failure, fall back to whatever static nodes we have.
    console.warn('Failed to fetch block metadata, using static fallback:', e)
  }
}

// ── Accessors ───────────────────────────────────────────────────

export function getCanvasNodeDef(type: string): CanvasNodeDefinition | undefined {
  return _registry[type]
}

/** Definitions available to add, given the current set of nodes and
 *  the insertion position. If insertAt is null, this is the trailing (+).
 *
 *  Rules are purely about relative ordering — no node kind is required:
 *    - Sources go at the top (before filters and prospect)
 *    - Filters go before prospect
 *    - Prospect goes before actions
 *    - Actions go at the bottom
 *  Any node kind can be the first node in the play. */
export function availableCanvasNodeDefs(
  existingNodes: CanvasNode[],
  insertAt?: number | null,
): { category: string; items: CanvasNodeDefinition[] }[] {
  const hasKind = (k: CanvasNodeKind) => existingNodes.some((n) => n.kind === k)
  const hasType = (t: string) => existingNodes.some((n) => n.type === t)

  // Find key node positions for ordering enforcement.
  const prospectIndex = existingNodes.findIndex((n) => n.kind === 'prospect')
  const firstFilterIndex = existingNodes.findIndex((n) => n.kind === 'filter')
  const firstActionIndex = existingNodes.findIndex((n) => n.kind === 'action')

  // For the trailing (+), determine what position we're effectively at.
  const effectiveInsert = insertAt ?? existingNodes.length

  // Is this position before/after key nodes?
  const isBeforeProspect = prospectIndex === -1 || effectiveInsert <= prospectIndex
  const isAfterProspect = prospectIndex !== -1 && effectiveInsert > prospectIndex
  const isBeforeFilters = firstFilterIndex === -1 || effectiveInsert <= firstFilterIndex
  const isAfterActions = firstActionIndex !== -1 && effectiveInsert > firstActionIndex

  const allowedKinds = new Set<CanvasNodeKind>()

  // Source: any number, each running independently; must be before filters/prospect.
  if (isBeforeProspect && isBeforeFilters) {
    allowedKinds.add('source')
  }

  // Filter: before prospect.
  if (isBeforeProspect && !isAfterActions) {
    allowedKinds.add('filter')
  }

  // Prospect: at most one, after filters, before actions.
  if (!hasKind('prospect') && !isAfterActions) {
    allowedKinds.add('prospect')
  }

  // Action: after prospect (or at the end if no prospect).
  if (isAfterProspect || !hasKind('prospect')) {
    allowedKinds.add('action')
  }

  const groups = new Map<string, CanvasNodeDefinition[]>()
  for (const def of Object.values(_registry)) {
    if (!allowedKinds.has(def.kind)) continue
    if (def.kind === 'filter' && def.type === 'icp' && hasType('icp')) continue
    if (def.kind !== 'source' && def.kind !== 'filter' && hasType(def.type)) continue
    const cat = def.addMenu.category
    if (!groups.has(cat)) groups.set(cat, [])
    groups.get(cat)!.push(def)
  }

  // Fixed category ordering.
  const categoryOrder = ['Find', 'Qualify', 'Prospect', 'Action']
  const result: { category: string; items: CanvasNodeDefinition[] }[] = []
  for (const cat of categoryOrder) {
    const items = groups.get(cat)
    if (items && items.length > 0) result.push({ category: cat, items })
  }
  // Append any categories not in the fixed order (future-proof).
  for (const [cat, items] of groups) {
    if (!categoryOrder.includes(cat) && items.length > 0) result.push({ category: cat, items })
  }
  return result
}

/** Definitions available to add within a specific group kind */
export function availableCanvasNodeDefsForKind(
  kind: CanvasNodeKind,
  existingNodes: CanvasNode[],
): CanvasNodeDefinition[] {
  const hasType = (t: string) => existingNodes.some((n) => n.type === t)
  const items: CanvasNodeDefinition[] = []
  for (const def of Object.values(_registry)) {
    if (def.kind !== kind) continue
    if (kind === 'filter' && def.type === 'icp' && hasType('icp')) continue
    if (kind !== 'source' && kind !== 'filter' && hasType(def.type)) continue
    if (kind === 'prospect' && existingNodes.some((n) => n.kind === 'prospect')) continue
    items.push(def)
  }
  return items
}

// ── Canvas node resolver (for the canvas flow) ─────────────────

export interface ResolvedCanvasNode {
  id: string
  type: string
  kind: CanvasNodeKind
  actionType?: 'sequence' | 'dialer'
  icon: string
  title: string
  subtitle: string
  operatesOn?: 'company' | 'person'
  detailsComponent?: Component
  detailsProps: { config: Record<string, any> }
}

/** Resolve a list of canvas nodes into render-ready objects. */
export function resolveCanvasNodes(nodes: CanvasNode[]): ResolvedCanvasNode[] {
  const resolved: ResolvedCanvasNode[] = []
  for (const node of nodes) {
    const def = _registry[node.type]
    if (!def) continue
    const summary = def.canvasCard.summarize(node.config)
    resolved.push({
      id: node.id,
      type: node.type,
      kind: node.kind,
      actionType: node.actionType,
      icon: def.canvasCard.icon,
      title: summary.title,
      subtitle: summary.subtitle,
      operatesOn: def.operatesOn,
      detailsComponent: def.canvasCard.detailsComponent,
      detailsProps: { config: node.config },
    })
  }
  return resolved
}

// ── Canvas drawer card resolver (for the context drawer) ────────

export interface ResolvedCanvasDrawerCard {
  id: string
  component: Component
  props: Record<string, any>
}

// ── Hydration (audience_definition → CanvasNode[]) ──────────────

/** Convert a pipeline's audience_definition into canvas nodes. */
export function hydrateCanvasNodes(audienceDef: Record<string, any> | null): CanvasNode[] {
  if (!audienceDef) return []
  const nodes: CanvasNode[] = []

  const produce = audienceDef.produce
  if (produce && typeof produce === 'object' && produce.type) {
    nodes.push({
      id: `source-${produce.type}-${Date.now()}`,
      type: produce.type,
      kind: 'source',
      config: { ...produce },
    })
  }

  const filters = audienceDef.filters
  if (Array.isArray(filters)) {
    for (let i = 0; i < filters.length; i++) {
      const f = filters[i]
      if (!f || !f.type) continue
      nodes.push({
        id: `filter-${f.type}-${i}-${Date.now()}`,
        type: f.type,
        kind: 'filter',
        config: { ...f },
      })
    }
  }

  return nodes
}

/** Resolve the drawer cards for a given canvas node type. */
export function resolveCanvasDrawerCards(
  nodeType: string,
  context: CanvasNodeContext,
): ResolvedCanvasDrawerCard[] {
  const def = _registry[nodeType]
  if (!def) return []
  return def.drawerCards.map((card) => ({
    id: card.id,
    component: card.component,
    props: card.resolveProps(context),
  }))
}
